import React from "react";
import { Box, Typography, Button, IconButton, Chip } from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import type { DynamicCase } from "../../types";

interface CaseCardProps {
  case: DynamicCase;
  onEdit: () => void;
  onDelete: () => void;
  onSemanticReview?: () => void;
}

export const CaseCard: React.FC<CaseCardProps> = ({
  case: caseItem,
  onEdit,
  onDelete,
  onSemanticReview,
}) => {
  // Helper functions
  const getStatusColor = (status: DynamicCase["processing_status"]) => {
    switch (status) {
      case "completed":
        return "#22c55e";
      case "processing":
        return "#f59e0b";
      case "pending":
        return "#6b7280";
      case "failed":
        return "#ef4444";
      case "review_required":
        return "#8b5cf6";
      default:
        return "#6b7280";
    }
  };

  const getStatusLabel = (status: DynamicCase["processing_status"]) => {
    switch (status) {
      case "completed":
        return "Completed";
      case "processing":
        return "Processing";
      case "pending":
        return "Pending";
      case "failed":
        return "Failed";
      case "review_required":
        return "Review Required";
      default:
        return status;
    }
  };

  const getDifficultyColor = (difficulty: DynamicCase["difficulty_level"]) => {
    switch (difficulty) {
      case "beginner":
        return "#22c55e";
      case "intermediate":
        return "#f59e0b";
      case "advanced":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // const canEdit = caseItem.processing_status === 'completed' || caseItem.processing_status === 'failed';
  const canEdit = true;
  const showProcessingIndicator = caseItem.processing_status === "processing";
  const showReviewIndicator = caseItem.processing_status === "review_required";

  return (
    // sx={{backgroundColor: '#1e293b'}}
    <Box
      className="management-case-card"
      sx={{
        backgroundColor: "background.paper",
        border: (theme) => `2px solid ${theme.palette.divider}`,
        padding: "1.5rem",
        borderRadius: "10px",
        height: "100%",
      }}
    >
      {/* Processing/Review Indicators */}
      {showProcessingIndicator && <div className="processing-indicator" />}
      {showReviewIndicator && <div className="review-required-indicator" />}

      {/* Card Header */}
      <Box className="case-card-header">
        <Typography
          variant="h6"
          className="case-card-title"
          sx={{
            color: "text.primary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            height: "60px",
          }}
        >
          {caseItem.title || "Untitled Case"}
        </Typography>
      </Box>

      {/* Case Preview */}
      {caseItem.preview && (
        <Typography
          variant="body2"
          className="case-card-preview"
          sx={{
            color: "text.primary",
            mb: 2,
            mt: 2,
            display: "-webkit-box",
            height: "60px",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {caseItem.preview}
        </Typography>
      )}

      {/* Case Details */}
      <Box
        className="case-card-details"
        sx={{
          backgroundColor: (theme) => theme.palette.grey[100],
          padding: "20px",
          borderRadius: "8px",
          marginTop: "10px",
        }}
      >
        <Box className="detail-item">
          <Typography variant="body2" className="detail-label">
            Age:
          </Typography>
          <Typography variant="body2" className="detail-value">
            {caseItem.age ? `${caseItem.age} years` : "Not specified"}
          </Typography>
        </Box>

        <Box className="detail-item">
          <Typography variant="body2" className="detail-label">
            Gender:
          </Typography>
          <Typography variant="body2" className="detail-value">
            {caseItem.gender || "Not specified"}
          </Typography>
        </Box>

        <Box className="detail-item">
          <Typography variant="body2" className="detail-label">
            Occupation:
          </Typography>
          <Typography variant="body2" className="detail-value">
            {caseItem.occupation || "Not specified"}
          </Typography>
        </Box>

        <Box className="detail-item">
          <Typography variant="body2" className="detail-label">
            Primary Condition:
          </Typography>
          <Typography variant="body2" className="detail-value">
            {caseItem.primary_condition || "Not specified"}
          </Typography>
        </Box>

        <Box className="detail-item">
          <Typography variant="body2" className="detail-label">
            Created:
          </Typography>
          <Typography variant="body2" className="detail-value">
            {formatDate(caseItem.created_at)}
          </Typography>
        </Box>

        <Box className="detail-item">
          <Typography variant="body2" className="detail-label">
            Author:
          </Typography>
          <Typography variant="body2" className="detail-value">
            {caseItem.author || "Unknown"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2, mb: 2, display: "flex", gap: 0.5 }}>
        <Chip
          label={getStatusLabel(caseItem.processing_status)}
          size="small"
          className={`status-badge ${caseItem.processing_status}`}
          sx={{
            backgroundColor: getStatusColor(caseItem.processing_status),
            color: "white",
            fontWeight: 600,
            fontSize: "0.7rem",
          }}
        />
        {caseItem.difficulty_level && (
          <Chip
            label={caseItem.difficulty_level}
            size="small"
            className={`difficulty-badge ${caseItem.difficulty_level}`}
            sx={{
              backgroundColor: getDifficultyColor(caseItem.difficulty_level),
              color: "white",
              fontWeight: 600,
              fontSize: "0.7rem",
              textTransform: "capitalize",
            }}
          />
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 0.5, flexDirection: "row" }}>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={onEdit}
          disabled={!canEdit}
          className="edit-btn"
          sx={{
            backgroundColor: canEdit ? "primary.main" : "action.disabled",
            color: "text.primary",
            fontWeight: 600,
            textTransform: "none",
            width: "90%",
            "&:hover": {
              backgroundColor: canEdit ? "primary.dark" : "action.disabled",
            },
            "&:disabled": {
              color: "text.disabled",
            },
          }}
        >
          Edit
        </Button>

        <Button
          onClick={onDelete}
          sx={{
            width: "10%",
            backgroundColor: "#ef4444",
            "&:hover": {
              backgroundColor: "error.main",
              color: "white",
            },
          }}
        >
          <DeleteIcon />
        </Button>
      </Box>

      {onSemanticReview && <Button fullWidth onClick={onSemanticReview} sx={{mt:1}}>Review semantic responses</Button>}

      {/* Processing Status Message */}
      {caseItem.processing_status === "processing" && (
        <Typography
          variant="caption"
          sx={{
            color: "warning.main",
            fontStyle: "italic",
            display: "block",
            textAlign: "center",
            mt: 1,
          }}
        >
          🔄 Processing in progress...
        </Typography>
      )}

      {caseItem.processing_status === "pending" && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontStyle: "italic",
            display: "block",
            textAlign: "center",
            mt: 1,
          }}
        >
          ⏳ Waiting to be processed...
        </Typography>
      )}

      {caseItem.processing_status === "failed" && (
        <Typography
          variant="caption"
          sx={{
            color: "error.main",
            fontStyle: "italic",
            display: "block",
            textAlign: "center",
            mt: 1,
          }}
        >
          ❌ Processing failed - edit to retry
        </Typography>
      )}

      {caseItem.processing_status === "review_required" && (
        <Typography
          variant="caption"
          sx={{
            color: "warning.main",
            fontStyle: "italic",
            display: "block",
            textAlign: "center",
            mt: 1,
          }}
        >
          Requires manual review. Case is not currently available in Diagnosis page.
        </Typography>
      )}
    </Box>
  );
};
